/**
 * Pre-Meeting Brief v2 API clients.
 *
 * Endpoints map 1:1 with controllers/pre_meeting_v2.js in api-getorbitx.
 * The /:brief_id/section, /:brief_id/section_order, /:brief_id/personalization,
 * and /rapid_people endpoints are called by py-processing (server-side
 * callbacks) and not from the FE — but they're still authenticated against
 * the user's token. All other data fetches (SQL, KOS, websearch, SerpAPI)
 * happen py-direct, no Node hop.
 */
import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"
import { PmbSectionResult } from "~/types/pre-meeting"

function _base(tokenInfo: ITokenInfo) {
  return `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting/v2`
}

// /pre_meeting/section sits one level above /v2
function _sectionBase(tokenInfo: ITokenInfo) {
  return `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/pre_meeting`
}

export interface PmbSectionCtx {
  accountId?: number | string | null
  apolloId?: string | null
  linkedinUrl?: string | null
  opportunityId?: number | string | null
  opportunityStage?: string | null
  personTitle?: string | null
  personContext?: {
    fullname?: string | null
    account_name?: string | null
    industry?: string | null
  }
}

export async function getPmbSection(
  tokenInfo: ITokenInfo,
  sectionId: string,
  ctx: PmbSectionCtx
): Promise<{ data: { success: boolean; data: PmbSectionResult } }> {
  return await axiosInstance({
    method: "POST",
    url: `${_sectionBase(tokenInfo)}/section`,
    headers: { Authorization: `Bearer ${tokenInfo?.token}` },
    data: {
      section_id: sectionId,
      account_id: ctx.accountId ?? null,
      apollo_id: ctx.apolloId ?? null,
      linkedin_url: ctx.linkedinUrl ?? null,
      opportunity_id: ctx.opportunityId ?? null,
      opportunity_stage: ctx.opportunityStage ?? null,
      person_title: ctx.personTitle ?? null,
      person_context: ctx.personContext ?? null
    }
  })
}

export interface PmbV2Personalization {
  mode: "personalized" | "default"
  q1?: string
  q3?: string | null
}

export interface PmbV2SectionOutput {
  content?: string | null
  payload?: Record<string, unknown> | null
  prompt_version?: string | null
  model?: string | null
  generated_at?: string | null
  error?: string | null
}

export interface PmbV2BriefSummary {
  brief_id: string
  session_id_v2: string
  meeting_context: string | null
  mode: "personalized" | "default" | null
  apollo_id: string | null
  created_at: string
}

export interface PmbV2BriefHydrated {
  brief_id: string
  session_id_v2: string
  personalization: PmbV2Personalization | null
  meeting_context: string | null
  section_order: string[]
  section_outputs: Record<string, PmbV2SectionOutput>
  apollo_id: string | null
  created_at: string
}

/**
 * POST /pre_meeting/v2/brief — pre-create a brief row keyed by ASKOS session_id.
 * Called from the entry-button handler before navigating to /os/agents/{session}.
 */
export async function createPmbV2Brief(
  tokenInfo: ITokenInfo,
  body: {
    session_id_v2: string
    /** Internal account id (integer) when known. */
    account_id?: number | string | null
    /** Apollo ObjectId — separate channel from account_id; either or both may be set. */
    apollo_id?: string | null
    linkedin_url?: string | null
    opportunity_id?: number | string | null
  }
) {
  return await axiosInstance({
    method: "POST",
    url: `${_base(tokenInfo)}/brief`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: body
  })
}

/**
 * GET /pre_meeting/v2/:brief_id — hydrate an existing brief.
 * Used on Previous Briefs row click + drawer reopen.
 */
export async function getPmbV2Brief(
  tokenInfo: ITokenInfo,
  brief_id: string
): Promise<{ data: { success: boolean; data: PmbV2BriefHydrated } }> {
  return await axiosInstance({
    method: "GET",
    url: `${_base(tokenInfo)}/${brief_id}`,
    headers: { Authorization: `Bearer ${tokenInfo?.token}` }
  })
}

/**
 * GET /pre_meeting/v2— list current user's v2 briefs (paged).
 */
export async function listPmbV2Briefs(
  tokenInfo: ITokenInfo,
  { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}
): Promise<{
  data: {
    success: boolean
    data: { briefs: PmbV2BriefSummary[]; limit: number; offset: number }
  }
}> {
  return await axiosInstance({
    method: "GET",
    url: `${_base(tokenInfo)}?limit=${limit}&offset=${offset}`,
    headers: { Authorization: `Bearer ${tokenInfo?.token}` }
  })
}

/**
 * GET /pre_meeting/v2/:brief_id/download — PDF download (returns blob).
 * NOTE: backend currently returns 501; PDF v2 template ships in Phase 7.
 */
export async function downloadPmbV2Brief(
  tokenInfo: ITokenInfo,
  brief_id: string
) {
  return await axiosInstance({
    method: "GET",
    url: `${_base(tokenInfo)}/${brief_id}/download`,
    headers: { Authorization: `Bearer ${tokenInfo?.token}` },
    responseType: "blob"
  })
}
